import { useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { useApolloClient } from '@frameio/graphql-client';
import type { ListLoadingStates } from '@frameio/vapor';
import { useQuickSearchModal } from '../../../shared/contexts';
import type { SectionId } from '../../../shared/types';
import type { GetQuickSearchResultsV2QueryVariables } from '../../gql/generated/GetQuickSearchResultsV2';
import { quickSearchTabbedMachine } from '../quickSearchTabbedMachine';
import type { QuickSearchTabbedOutput } from '../types';
import { useInitialSearchNodes } from './useInitialSearchNodes';

type UseQuickSearchMachineTabbedProps = {
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  skip: boolean;
  isSearchQueryStale: boolean;
  isInitial: boolean;
};

export const useQuickSearchMachineTabbed = ({
  requestVariables,
  skip,
  isSearchQueryStale,
  isInitial,
}: UseQuickSearchMachineTabbedProps): QuickSearchTabbedOutput => {
  const apolloClient = useApolloClient();
  const { scopedToProjectId } = useQuickSearchModal();

  const { initialLexicalNodes, initialNlpNodes } = useInitialSearchNodes({
    requestVariables,
    skip,
    isInitial,
    listMode: 'MULTI_TABBED',
  });

  const [state, send] = useMachine(quickSearchTabbedMachine, {
    input: {
      apolloClient,
      requestVariables,
      initialLexicalNodes,
      initialNlpNodes,
      scopedToProjectId,
    },
  });

  useEffect(() => {
    const hasInitialNodes =
      initialLexicalNodes !== undefined || initialNlpNodes !== undefined;

    if (skip || (isInitial && hasInitialNodes)) {
      return;
    }

    send({
      type: 'SEARCH',
      requestVariables,
      scopedToProjectId,
    });
  }, [
    requestVariables,
    send,
    skip,
    isInitial,
    initialLexicalNodes,
    initialNlpNodes,
    scopedToProjectId,
  ]);

  const isLexicalLoading =
    isSearchQueryStale ||
    state.matches({
      searching: {
        parallelSearching: { lexicalQuery: 'lexLoading' },
      },
    });

  const isNlpLoading =
    isSearchQueryStale ||
    state.matches({
      searching: {
        parallelSearching: { nlpQuery: 'nlpLoading' },
      },
    });

  const loadingStatus = useMemo((): ListLoadingStates | undefined => {
    if (isSearchQueryStale) {
      return 'loading';
    }

    if (state.matches('complete')) {
      if (
        state.context.lexicalError !== undefined &&
        state.context.nlpError !== undefined
      ) {
        return 'loading-error';
      }

      return undefined;
    }

    if (state.matches({ searching: { parallelSearching: {} } })) {
      const isLexLoading = state.matches({
        searching: {
          parallelSearching: { lexicalQuery: 'lexLoading' },
        },
      });
      const isLexSuccess = state.matches({
        searching: {
          parallelSearching: { lexicalQuery: 'lexSuccess' },
        },
      });
      const isLexError = state.matches({
        searching: {
          parallelSearching: { lexicalQuery: 'lexError' },
        },
      });

      const isNlpQueryLoading = state.matches({
        searching: {
          parallelSearching: { nlpQuery: 'nlpLoading' },
        },
      });

      if (isLexLoading || (isLexError && isNlpQueryLoading)) {
        return 'loading';
      }

      if (
        isLexSuccess &&
        state.context.lexicalNodes.length === 0 &&
        isNlpQueryLoading
      ) {
        return 'loading';
      }

      if (
        isLexSuccess &&
        state.context.lexicalNodes.length > 0 &&
        isNlpQueryLoading
      ) {
        return 'loading-more';
      }
    }

    return undefined;
  }, [state, isSearchQueryStale]);

  /**
   * The SEARCH event is dispatched from an effect, so on the render where
   * requestVariables changes, the machine's context/counts still reflect the
   * previous query for one render. Comparing context.requestVariables (set by
   * the SEARCH transition) to the current requestVariables prop closes that
   * gap so consumers never see "settled" paired with a stale query's counts.
   */
  const hasQueriesSettled =
    state.matches('complete') &&
    state.context.requestVariables === requestVariables;
  const { lexicalNodes, nlpNodes } = state.context;

  const tabItemsById = useMemo(
    (): Record<SectionId, typeof lexicalNodes> => ({
      lexical: loadingStatus === 'loading' ? [] : lexicalNodes,
      nlp: loadingStatus === 'loading' ? [] : nlpNodes,
    }),
    [lexicalNodes, nlpNodes, loadingStatus]
  );

  const tabLoadingStatusBySectionId = useMemo(() => {
    const resolveTabLoadingStatus = (
      isTabLoading: boolean,
      itemsCount: number
    ): ListLoadingStates | undefined => {
      if (isTabLoading) {
        return itemsCount > 0 ? 'loading-more' : 'loading';
      }

      if (loadingStatus === 'loading-error') return 'loading-error';
      return undefined;
    };

    return {
      lexical: resolveTabLoadingStatus(isLexicalLoading, lexicalNodes.length),
      nlp: resolveTabLoadingStatus(isNlpLoading, nlpNodes.length),
    };
  }, [
    loadingStatus,
    isLexicalLoading,
    isNlpLoading,
    lexicalNodes.length,
    nlpNodes.length,
  ]);

  const standardResultsCount = lexicalNodes.length;
  const mediaIntelligenceResultsCount = nlpNodes.length;
  const isNlpOnlyMode =
    hasQueriesSettled &&
    standardResultsCount === 0 &&
    mediaIntelligenceResultsCount > 0;

  return {
    listMode: 'MULTI_TABBED',
    isLexicalLoading,
    isNlpLoading,
    isNlpOnlyMode,
    tabItemsById,
    tabLoadingStatusBySectionId,
    standardResultsCount,
    mediaIntelligenceResultsCount,
    hasQueriesSettled,
  };
};
